import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { openableRow } from '../../lib/openableRow'
import { apiRating, DashClient, DashboardResponse, durationText, QueueFigures, QueuesResponse, Rating, VERSION_RE } from './api'
import base from './GodMode.module.css'
import styles from './DashboardPage.module.css'
import { SystemLoad } from './SystemLoad'

// God Mode's first page: what is happening at every client today, each
// figure coloured by how it stands (services/god-dashboard.js has the rules),
// the alerts that need someone, and the API's own health. Asks again every
// minute while the tab is open; real clients first, demo and test ones last.
// A row opens that client's own Control as the support sign-in (as the client
// page's Open workspace does); one this staff member cannot open goes to the
// client page instead.

const EVERY_MS = 60_000
const QUEUES_EVERY_MS = 5_000 // the processing queues alone, so they count down live

const LABEL: Record<Rating, string> = { ok: 'OK', idle: 'Quiet', warn: 'Check', bad: 'Problem' }
const MARK: Record<Rating, string> = { ok: '✓', idle: '–', warn: '!', bad: '✕' }

export function ago(min: number | null) {
  if (min === null) return '—'
  if (min < 1) return 'just now'
  if (min < 60) return `${min} min ago`
  if (min < 48 * 60) return `${Math.floor(min / 60)} h ${min % 60 ? `${min % 60} min ` : ''}ago`
  return `${Math.floor(min / 1440)} days ago`
}

export function StatusPill({ status, children }: { status: Rating; children?: React.ReactNode }) {
  return (
    <span className={`${styles.pill} ${styles[status]}`}>
      <span aria-hidden="true">{MARK[status]}</span> {children ?? LABEL[status]}
    </span>
  )
}

const kg = (n: number) => n.toLocaleString('en-NZ', { maximumFractionDigits: 1 })

function phonesText(c: DashClient) {
  const total = c.phones.reduce((n, p) => n + p.phones, 0)
  if (!total) return null
  return c.phones.map((p) => `${p.phones} ${p.platform}${p.version ? ` ${VERSION_RE.test(p.version) ? 'version' : 'build'} ${p.version}` : ''}`).join(', ')
}

const RANK: Record<Rating, number> = { ok: 0, idle: 0, warn: 1, bad: 2 }

// The things to chase up after the day: refused Crew records, photos that never
// arrived, QC scans matched to no item, days not closed, wage recalculations
// and records not through the Cycle Engine.
function followUps(c: DashClient) {
  const s = c.status
  const out: { text: string; to?: string }[] = []
  if (c.refusals.last_day) out.push({ text: `${c.refusals.last_day} refused`, to: `/god-mode/clients/${c.id}/refusals` })
  if (c.photos_missing.photos) out.push({ text: `${c.photos_missing.photos} photo${c.photos_missing.photos === 1 ? '' : 's'} missing` })
  if (c.unmatched_qc) out.push({ text: `${c.unmatched_qc} QC unmatched`, to: `/god-mode/clients/${c.id}/qc` })
  if (c.open_days.days) out.push({ text: `${c.open_days.days} day${c.open_days.days === 1 ? '' : 's'} open` })
  if (c.wage_queue.errors || c.wage_queue.stuck) out.push({ text: `${c.wage_queue.errors + c.wage_queue.stuck} wage recalc` })
  if (s.backlog !== 'ok') out.push({ text: `${c.backlog.pending} not processed` })
  const rating = [s.refusals, s.photos, s.qc, s.days, s.wages, s.backlog].reduce<Rating>((a, b) => (RANK[b] > RANK[a] ? b : a), 'ok')
  return { out, rating }
}

function ClientRow({ c, onOpen, canOpen }: { c: DashClient; onOpen: () => void; canOpen: boolean }) {
  const s = c.status
  const cell = (r: Rating) => styles[`cell_${r}`]
  const phones = c.phones.reduce((n, p) => n + p.phones, 0)
  const follow = followUps(c)
  const navigate = useNavigate()
  const firstTo = follow.out.find((f) => f.to)?.to
  return (
    <tr
      className={base.rowLink}
      {...openableRow(onOpen)}
      title={canOpen ? `Open ${c.name}'s Control` : `${c.name}'s client page`}
      data-testid={`dash-${c.id}`}
    >
      <td>
        <StatusPill status={s.overall} />
      </td>
      <td>
        <strong>{c.name}</strong>
        <div className={styles.sub}>
          {c.local_time} {c.work_hours ? '' : '· outside work hours'} ·{' '}
          <Link to={`/god-mode/clients/${c.id}`} onClick={(e) => e.stopPropagation()}>
            Client page
          </Link>
        </div>
      </td>
      <td className={cell(s.picks)}>
        {c.picks.items ? (
          <>
            <strong>{kg(c.picks.kg)} kg</strong> · {c.picks.items} items
            <div className={styles.sub}>
              {c.picks.pickers} picker{c.picks.pickers === 1 ? '' : 's'} · last {ago(c.picks.last_pick_min)}
            </div>
          </>
        ) : (
          <span className={styles.none}>None today</span>
        )}
      </td>
      <td className={cell(s.data)}>
        {ago(c.data.last_min)}
        <div className={styles.sub}>{c.data.records_today} records today</div>
      </td>
      <td className={cell(s.people)}>
        <strong>{c.people.on_now}</strong> on now
        <div className={styles.sub}>
          {c.people.today} today{c.people.missing_clock_offs ? ` · ${c.people.missing_clock_offs} not clocked off` : ''}
        </div>
      </td>
      <td>
        {c.preassigns.today || <span className={styles.none}>0</span>}
        {c.preassigns.waiting > 0 && <div className={styles.sub}>{c.preassigns.waiting} not weighed</div>}
      </td>
      <td className={cell(s.phones)} title={phonesText(c) || undefined}>
        {phones ? (
          <>
            {phones} phone{phones === 1 ? '' : 's'}
            <div className={styles.sub}>{phonesText(c)}</div>
          </>
        ) : (
          <span className={styles.none}>None</span>
        )}
      </td>
      <td className={cell(s.stations)}>
        {c.stations.total ? (
          <>
            {c.stations.recent} / {c.stations.total} live
            <div className={styles.sub}>last {ago(c.stations.last_min)}</div>
          </>
        ) : (
          <span className={styles.none}>None</span>
        )}
      </td>
      <td>{c.data.quality_today || <span className={styles.none}>0</span>}</td>
      {/* The cell is its own target: a click that misses a line's words goes to
          the first issue with a page rather than opening the client. */}
      <td
        className={`${cell(follow.rating)} ${styles.follow}`}
        onClick={(e) => {
          e.stopPropagation()
          if (firstTo) navigate(firstTo)
        }}
        style={firstTo ? { cursor: 'pointer' } : { cursor: 'default' }}
      >
        {follow.out.length ? (
          follow.out.map((f) => (
            <div key={f.text}>
              {f.to ? (
                <Link to={f.to} onClick={(e) => e.stopPropagation()}>
                  {f.text}
                </Link>
              ) : (
                f.text
              )}
            </div>
          ))
        ) : (
          <span className={styles.none}>Nothing</span>
        )}
      </td>
    </tr>
  )
}

function ClientTable({ clients, open, canOpen }: { clients: DashClient[]; open: (c: DashClient) => void; canOpen: (c: DashClient) => boolean }) {
  return (
    <div className={base.tableCard}>
      <table className={base.table}>
        <thead>
          <tr>
            <th>Status</th>
            <th>Client</th>
            <th>Picked today</th>
            <th title="When anything last arrived from this client">Last data</th>
            <th>Workers</th>
            <th title="Items with a name on them today">Preassigns</th>
            <th title="Dataphyll Crew phones seen in the last day">Crew phones</th>
            <th title="Seen in the last 30 minutes, of all active">Field stations</th>
            <th>QC today</th>
            <th title="Refused Crew records, photos not arrived, QC scans matched to no item, days not closed, wage recalculations">Follow up</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => (
            <ClientRow key={c.id} c={c} onOpen={() => open(c)} canOpen={canOpen(c)} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

// The work Control does in the background, per client with any of it waiting:
// incoming data (picks, receipts, preassigns, QC scans from phones and stations)
// for the Cycle Engine, wage recalculations (set off by new data, rate and QC
// corrections, merges), and emails. Failed incoming data and wage recalcs stay
// queued and are tried again; a failed email has given up.
function Queued({ n, failed, oldest }: { n: number; failed: number; oldest?: number | null }) {
  if (!n && !failed) return <>—</>
  return (
    <>
      {n.toLocaleString()}
      {failed > 0 && <> ({failed.toLocaleString()} failed)</>}
      {oldest !== undefined && n > 0 && <div className={styles.sub}>oldest {ago(oldest)}</div>}
    </>
  )
}

function ProcessingQueues({ clients: dashboard }: { clients: DashClient[] }) {
  const { user } = useAuth()
  const [live, setLive] = useState<Record<string, QueueFigures>>({})
  useEffect(() => {
    let gone = false
    const poll = async () => {
      if (document.visibilityState === 'hidden') return
      try {
        const r = await apiJson<QueuesResponse>(user, '/api/god-mode/queues')
        if (!gone) setLive(Object.fromEntries(r.clients.map((q) => [q.id, q])))
      } catch {
        // The dashboard's own minute-old figures stand; its refresh reports any outage.
      }
    }
    poll()
    const t = setInterval(poll, QUEUES_EVERY_MS)
    return () => {
      gone = true
      clearInterval(t)
    }
  }, [user])
  const clients = dashboard.map((c) => (live[c.id] ? { ...c, ...live[c.id] } : c))
  const busy = (c: DashClient) => c.backlog.pending + c.wage_queue.queued + c.emails.waiting + c.emails.failed > 0
  const oldest = (c: DashClient) => Math.max(c.backlog.oldest_min ?? 0, c.wage_queue.oldest_min ?? 0)
  const rows = clients.filter(busy).sort((a, b) => oldest(b) - oldest(a))
  const total = rows.reduce((n, c) => n + c.backlog.pending + c.wage_queue.queued + c.emails.waiting, 0)
  // Today's processing times, for every client with any (the dashboard's one-minute read).
  const timed = clients.filter((c) => c.timing && (c.timing.incoming.timed || c.timing.wages.done)).sort((a, b) => a.name.localeCompare(b.name))
  const rating = (c: DashClient): Rating =>
    c.backlog.failed || c.wage_queue.errors || c.emails.failed
      ? 'bad'
      : [c.status.backlog, c.status.wages].reduce<Rating>((a, b) => (RANK[b] > RANK[a] ? b : a), 'ok')
  return (
    <section className={base.card} aria-label="Processing queues">
      <h2>Processing queues{total ? `: ${total.toLocaleString()} waiting` : ''}</h2>
      <p className={styles.sub}>
        Live: updated every {QUEUES_EVERY_MS / 1000} seconds. <Link to="/god-mode/queues">Every item in Queues</Link>
      </p>
      <p className={base.lead}>
        Incoming data is picks, receipts, preassigns and QC scans from Crew phones, Field devices and stations, received but not yet
        processed. Wage recalculations follow new data, rate and QC corrections and merges. Failed emails are those that gave up in the
        last day.
      </p>
      {rows.length === 0 ? (
        <p className={base.lead}>Nothing waiting: all caught up.</p>
      ) : (
        <table className={base.table}>
          <thead>
            <tr>
              <th>Status</th>
              <th>Client</th>
              <th>Incoming data</th>
              <th>Wage recalculations</th>
              <th>Emails</th>
              <th>Last error</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <StatusPill status={rating(c)} />
                </td>
                <td>{c.name}</td>
                <td>
                  <Queued n={c.backlog.pending} failed={c.backlog.failed} oldest={c.backlog.oldest_min} />
                </td>
                <td>
                  <Queued n={c.wage_queue.queued} failed={c.wage_queue.errors} oldest={c.wage_queue.oldest_min} />
                </td>
                <td>
                  <Queued n={c.emails.waiting} failed={c.emails.failed} />
                </td>
                <td>{c.backlog.last_error ?? c.wage_queue.last_error ?? c.emails.last_error ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {timed.length > 0 && (
        <>
          <h3>Processing times today</h3>
          <p className={base.lead}>
            Took is the Cycle Engine's own time on a record; waited is from received to done. Each client's day, updated every minute.
          </p>
          <table className={base.table} aria-label="Processing times today">
            <thead>
              <tr>
                <th>Client</th>
                <th>Incoming data done</th>
                <th>Median took</th>
                <th>Slowest</th>
                <th>Median waited</th>
                <th>Wage recalculations done</th>
                <th>Average took</th>
                <th>Slowest</th>
              </tr>
            </thead>
            <tbody>
              {timed.map((c) => {
                const { incoming: i, wages: w } = c.timing!
                return (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{i.timed ? i.timed.toLocaleString() : '—'}</td>
                    <td>{durationText(i.median_ms)}</td>
                    <td>{durationText(i.max_ms)}</td>
                    <td>{durationText(i.median_wait_s === null ? null : i.median_wait_s * 1000)}</td>
                    <td>{w.done ? w.done.toLocaleString() : '—'}</td>
                    <td>{durationText(w.average_ms)}</td>
                    <td>{durationText(w.max_ms)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { subscriptions, selectWorkspace } = useWorkspace()
  const canOpen = (c: DashClient) => subscriptions.some((s) => s.id === c.id)
  const open = (c: DashClient) => {
    if (!canOpen(c)) return navigate(`/god-mode/clients/${c.id}`)
    selectWorkspace(c.id)
    navigate('/dashboard')
  }
  const [data, setData] = useState<DashboardResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setBusy(true)
    try {
      setData(await apiJson<DashboardResponse>(user, '/api/god-mode/dashboard'))
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }, [user])

  useEffect(() => {
    load()
    const t = setInterval(() => {
      if (document.visibilityState !== 'hidden') load()
    }, EVERY_MS)
    return () => clearInterval(t)
  }, [load])

  const clients = data?.clients || []
  const real = clients.filter((c) => !c.demo)
  const demo = clients.filter((c) => c.demo)
  const alerts = real
    .flatMap((c) => c.alerts.map((a) => ({ ...a, client: c })))
    .sort((a, b) => (a.status === b.status ? 0 : a.status === 'bad' ? -1 : 1))
  const sys = data?.system
  const dbStatus: Rating = !sys ? 'idle' : sys.db_ms > 1000 ? 'bad' : sys.db_ms > 200 ? 'warn' : 'ok'
  const onNow = real.reduce((n, c) => n + c.people.on_now, 0)
  const kgToday = real.reduce((n, c) => n + c.picks.kg, 0)

  return (
    <>
      <PageHeader
        title="Dashboard"
        actions={
          <button type="button" className={base.secondary} onClick={load} disabled={busy}>
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />
      <PageBody>
        <div className={base.stack}>
          {error && (
            <p className={base.error} role="alert">
              Could not read the dashboard: {error}
              {data ? ` (showing what was read at ${new Date(data.at).toLocaleTimeString()})` : ''}
            </p>
          )}
          {!data && !error && <p className={base.lead}>Loading…</p>}
          {data && (
            <>
              <section className={styles.tiles} aria-label="System">
                <div className={styles.tile}>
                  <span className={styles.tileLabel}>API</span>
                  <StatusPill status={error ? 'bad' : 'ok'}>{error ? 'Not answering' : 'Up'}</StatusPill>
                  <span className={styles.sub}>
                    {sys!.revision || 'local'} · up {ago(sys!.uptime_min).replace(' ago', '')}
                  </span>
                </div>
                {data.api && (
                  <div className={styles.tile}>
                    <span className={styles.tileLabel}>
                      <Link to="/god-mode/api">Requests, last hour</Link>
                    </span>
                    <StatusPill status={apiRating(data.api.totals)}>
                      {data.api.totals.n ? `95% within ${durationText(data.api.totals.p95_ms)}` : 'None'}
                    </StatusPill>
                    <span className={styles.sub}>
                      {data.api.totals.n.toLocaleString()} · {data.api.totals.failed.toLocaleString()} failed · {data.api.in_flight.now} running now
                    </span>
                  </div>
                )}
                <div className={styles.tile}>
                  <span className={styles.tileLabel}>Database</span>
                  <StatusPill status={dbStatus}>{sys!.db_ms} ms</StatusPill>
                  <span className={styles.sub}>dashboard read in {sys!.read_ms} ms</span>
                </div>
                <div className={styles.tile}>
                  <span className={styles.tileLabel}>Live updates</span>
                  <StatusPill status={sys!.live ? 'ok' : 'warn'}>{sys!.live ? 'Listening' : 'Off'}</StatusPill>
                  <span className={styles.sub}>screens refresh as data lands</span>
                </div>
                <div className={styles.tile}>
                  <span className={styles.tileLabel}>Last data, any client</span>
                  <StatusPill status={sys!.last_data_min === null ? 'idle' : 'ok'}>{ago(sys!.last_data_min)}</StatusPill>
                  <span className={styles.sub}>
                    {onNow} on now · {kg(kgToday)} kg today
                  </span>
                </div>
                <div className={styles.tile}>
                  <span className={styles.tileLabel}>Alerts</span>
                  <StatusPill status={alerts.some((a) => a.status === 'bad') ? 'bad' : alerts.length ? 'warn' : 'ok'}>
                    {alerts.length ? `${alerts.length} open` : 'None'}
                  </StatusPill>
                  <span className={styles.sub}>updated {new Date(data.at).toLocaleTimeString()}</span>
                </div>
              </section>

              {alerts.length > 0 && (
                <section className={base.card} aria-label="Alerts">
                  <h2>Needs a look</h2>
                  <ul className={styles.alerts}>
                    {alerts.map((a, i) => (
                      <li key={`${a.client.id}-${a.key}-${i}`}>
                        <StatusPill status={a.status} />
                        <a
                          href={`/god-mode/clients/${a.client.id}`}
                          onClick={(e) => {
                            e.preventDefault()
                            open(a.client)
                          }}
                        >
                          {a.client.name}
                        </a>
                        {a.key === 'refusals' || a.key === 'qc' ? (
                          <Link to={`/god-mode/clients/${a.client.id}/${a.key === 'qc' ? 'qc' : 'refusals'}`}>{a.text}</Link>
                        ) : (
                          <span>{a.text}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {clients.length === 0 ? (
                <div className={base.tableCard}>
                  <div className={base.empty}>
                    No clients yet. <Link to="/god-mode/new">Create the first one</Link>.
                  </div>
                </div>
              ) : (
                real.length > 0 && <ClientTable clients={real} open={open} canOpen={canOpen} />
              )}

              {demo.length > 0 && (
                <>
                  <h2 className={styles.heading}>Demo and test clients</h2>
                  <ClientTable clients={demo} open={open} canOpen={canOpen} />
                </>
              )}

              <ProcessingQueues clients={clients} />

              <SystemLoad />

              <p className={base.lead}>
                Dataphyll's own people and phones are left out. "Gone quiet" counts only from 6 am to 6 pm on each client's clock, while
                someone there is clocked on. Phones are flagged when they run an older Crew build than the newest seen
                {Object.keys(data.newest_builds).length
                  ? ` (${Object.entries(data.newest_builds)
                      .map(([p, b]) => `${p} ${b}`)
                      .join(', ')})`
                  : ''}
                .
              </p>
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}
